import { Component, ChangeDetectionStrategy, AfterContentInit, AfterViewInit } from '@angular/core';
import { SectionFormDirective } from '@app/classes/section-form.class';
import { FormBuilder } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { formatTableData } from '@app/helpers/tables.helpers';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { getDataValue } from "@app/helpers/object.helpers";
import { equals, isNil, isEmpty } from 'ramda';
import { ApiService } from "@app/gateway/services/api.service";
import { ProcessService } from "@app/services/process.service";
import { ModalService } from "@app/services/modal/modal.service";
import { isMilitary } from '@app/helpers/models.helpers';
import { disableResetControls, enableResetControls, resetDisableControls, resetEnableControls } from '@app/helpers/reset-able.helpers';
import Vs from '@app/services/validators/validators.service';
import { HttpHeaders } from '@angular/common/http';
import { TokenService } from '@app/services/token/token.service';


@Component({
  templateUrl: './medical-appeal-demographics.component.html',
  styleUrls: ['./medical-appeal-demographics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class MedicalAppealDemographicsComponent extends SectionFormDirective implements AfterContentInit, AfterViewInit {

  translatedLang;
  defaultLanguage;
  toolTip: boolean;
  householdMember;
  filer;
  filingDate;
  existingHouseholdMemberAddress;
  householdZip;
  addressFormat;
  addressSub: Subscription;
  isMilitary;
  addressFormatValue;
  validAddress = true;
  interpreterMandatory = false;
  showHouseholdAddressForm: boolean = false;


  constructor(public builder: FormBuilder, private titleService: Title, public translateService: TranslateService, public processService: ProcessService, public apiService: ApiService, private modalService: ModalService, private token: TokenService) {
    super(builder);
    this.addressSub = this.processService.processAddressValidate.subscribe((action) => this.addressValidate(action));
    this.titleService.setTitle('Medical Appeal Demographics');
  };


  addressValidate(action) {
    let postObject;
    const appNum = this.applicationNumber;
    const formData = this.formGroup.value.data;
    const medAppealAddress = getDataValue('medAppealAddress.address', formData);
    const isSaveExit = !!equals(action, 'fromSaveExit');

    postObject = {
      addressList: [
        { addressType: 'PHYSICAL', originalAddress: medAppealAddress },
      ]
    }

    if (postObject && isEmpty(postObject.addressList)) {
      this.processService.addressValidated = true;
    } else {
      const contentHeader = new HttpHeaders({ 'pageId': 'MADEM', 'sessionId': this.token.getSessionId() })
      this.apiService.post('applications/' + appNum + '/sections/MADEM/address', postObject, { headers: contentHeader }, true).subscribe((res) => {
        if (!isNil(res)) {
          this.modalService.openAddressModal(res, isSaveExit).subscribe(result => {
            if (result !== true) {
              if (result) {
                const phyIndex = result.findIndex(i => i.addressType === 'PHYSICAL');
                if (phyIndex === 0) {
                  const addressLine1 = this.getControl('data.medAppealAddress.address.addressLine1', this.formGroup);
                  const addressLine2 = this.getControl('data.medAppealAddress.address.addressLine2', this.formGroup);
                  const city = this.getControl('data.medAppealAddress.address.city', this.formGroup);
                  const state = this.getControl('data.medAppealAddress.address.state', this.formGroup);
                  const zip = this.getControl('data.medAppealAddress.address.zip', this.formGroup);
                  const addressValidationCd = this.getControl('data.medAppealAddress.addressValidationCd', this.formGroup);

                  if (result[0].selectedAddress === 'Suggested') {
                    addressLine1.setValue(result[0].suggestedAddress.addressLine1);
                    addressLine2.setValue(result[0].suggestedAddress.addressLine2);
                    city.setValue(result[0].suggestedAddress.city);
                    state.setValue(result[0].originalAddress.state);
                    zip.setValue(result[0].suggestedAddress.zip);
                    addressValidationCd.setValue('ESA');
                  } else if (result[0].selectedAddress === 'Original') {
                    addressLine1.setValue(result[0].originalAddress.addressLine1);
                    addressLine2.setValue(result[0].originalAddress.addressLine2);
                    city.setValue(result[0].originalAddress.city);
                    state.setValue(result[0].originalAddress.state);
                    zip.setValue(result[0].originalAddress.zip);
                    addressValidationCd.setValue('MEA');
                  }
                };
                this.processService.addressValidated = true;
                sessionStorage.setItem('mademAddressValidated', 'true');
              }
            }
          });
        } else {
          this.processService.addressValidated = true;
          sessionStorage.setItem('mademAddressValidated', 'true');
          this.getControl('data.medAppealAddress.addressValidationCd', this.formGroup)?.setValue(null);
        }
      });
    }
  }

  ngAfterContentInit() {
    this.defaultLanguage = this.translateService.getDefaultLang();
    this.translatedLang = this.defaultLanguage;
    this.languageCheck(this.defaultLanguage);
    this.translateService.onDefaultLangChange.subscribe((res) => {
      this.translatedLang = res.lang;
      this.languageCheck(res.lang);
    });

    this.householdMember = this.getValue('data.householdMember');
    this.filer = this.getValue('data.filer');
    this.filingDate = this.getValue('data.filingDate');

    this.existingHouseholdMemberAddress = this.getValue('data.medAppealAddress.address');
    if (this.existingHouseholdMemberAddress?.zip && this.existingHouseholdMemberAddress?.zip.length > 5) {
      this.householdZip = `${this.existingHouseholdMemberAddress.zip.substr(0, 5)}-${this.existingHouseholdMemberAddress.zip.substr(5, 4)}`;
    } else {
      this.householdZip = this.existingHouseholdMemberAddress.zip;
    }
    this.addressFormat = this.getControl('data.medAppealAddress.addressFormat');
    this.addressFormatValue = this.getValue('data.medAppealAddress.addressFormat')
    this.addressFormatChange('onLoad');
    const medAppealAddress = this.getControl('data.medAppealAddress.address');
    if (medAppealAddress?.value?.state?.code !== 'TN') {
      this.getControl('data.medAppealAddress.address.county').clearValidators();
      this.getControl('data.medAppealAddress.address.county').updateValueAndValidity();
    }
    medAppealAddress.valueChanges.pipe().subscribe(() => {
      this.processService.addressValidated = false;
      this.validAddress = medAppealAddress.status === 'VALID' ? true : false;
    });

    if (sessionStorage.getItem('mademAddressValidated') === 'true') {
      this.processService.addressValidated = true;
    }

    if(this.addressFormatValue?.code === 'MI') {
      const addressValidationCd = this.getControl('data.medAppealAddress.addressValidationCd', this.formGroup);
      addressValidationCd.setValue('MEA');
    }

    // Initialize conditional validators based on existing data
    this.isShowInterpreterHearingFields(true);
    this.spokenLang(true);
    this.needInterpreter(true);
    this.hearingLang(true);
  }


  ngAfterViewInit() {
    setTimeout(() => {
      const heading = document.querySelector('h1.page-title-h1') as HTMLElement | null;
      if (heading) {
        heading.style.outline = 'none';
        heading.focus();
      }
    }, 100);
  }

  
  languageCheck(language) {
    this.tables = language === 'sp' ? formatTableData(this.tables, 'spDescription') : formatTableData(this.tables, 'description');
  }

  tableCodeValue(tableName, tableControl) {
    return this.getTableCodeValues(tableName, tableControl, this.translatedLang, false)
  }


  addressFormatChange(info) {
    const addressFormatCode = this.getValue('data.medAppealAddress.addressFormat');
    const addressControl = this.getControl('data.medAppealAddress.address')
    if (addressFormatCode.code !== 'MI') {
      this.getControl('data.medAppealAddress.address.addrAaAeAp').clearValidators();
      this.getControl('data.medAppealAddress.address.addrAaAeAp').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.addrApoFpo').clearValidators();
      this.getControl('data.medAppealAddress.address.addrApoFpo').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.city').setValidators([Vs.required, Vs.maCity, Vs.maxLength(25)]);
      this.getControl('data.medAppealAddress.address.city').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.state').setValidators(Vs.required);
      this.getControl('data.medAppealAddress.address.state').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.county').setValidators(Vs.required);
      this.getControl('data.medAppealAddress.address.county').updateValueAndValidity();
    } else {
      //this.getControl('data.medAppealAddress.address').reset();
      this.getControl('data.medAppealAddress.address.city').clearValidators();
      this.getControl('data.medAppealAddress.address.city').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.state').clearValidators();
      this.getControl('data.medAppealAddress.address.state').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.county').clearValidators();
      this.getControl('data.medAppealAddress.address.county').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.addrAaAeAp').setValidators(Vs.required);
      this.getControl('data.medAppealAddress.address.addrAaAeAp').updateValueAndValidity();
      this.getControl('data.medAppealAddress.address.addrApoFpo').setValidators(Vs.required);
      this.getControl('data.medAppealAddress.address.addrApoFpo').updateValueAndValidity();
    }
    this.getControl('data.medAppealAddress.address.addressLine1').setValidators([Vs.required, Vs.maAddressLine1, Vs.maxLength(46)]);
    this.getControl('data.medAppealAddress.address.addressLine1').updateValueAndValidity();
    this.getControl('data.medAppealAddress.address.addressLine2').setValidators([Vs.maAddressLine2, Vs.maxLength(14)]);
    this.getControl('data.medAppealAddress.address.addressLine2').updateValueAndValidity();
    if (info === 'onChange') {
      const city = this.getControl('data.medAppealAddress.address.city');
      const state = this.getControl('data.medAppealAddress.address.state');
      const county = this.getControl('data.medAppealAddress.address.county');
      const zip = this.getControl('data.medAppealAddress.address.zip');
      const addrApoFpo = this.getControl('data.medAppealAddress.address.addrApoFpo');
      const addrAaAeAp = this.getControl('data.medAppealAddress.address.addrAaAeAp');
      const whenMilitary = [addrApoFpo, addrAaAeAp];
      const whenNotMilitary = [city, state, county];
      this.isMilitary = isMilitary(addressFormatCode);
      if (addressFormatCode) {
        if (addressControl.status === 'DISABLED') {
          return null;
        } else {
          if (this.isMilitary) {
            resetEnableControls(whenMilitary);
            resetDisableControls(whenNotMilitary);
            zip.reset()
          } else {
            enableResetControls(whenNotMilitary);
            disableResetControls(whenMilitary);
            zip.reset();
            county?.markAsPristine();
            county?.markAsUntouched();
          }
        }
      }
    }
  }

  
  writtenLang(isInit = false) {
    this.isShowInterpreterHearingFields(isInit);
  }

  spokenLang(isInit = false) {
    this.isShowInterpreterHearingFields(isInit);
    const spokenLang = this.getValue('data.indvLangPrefCd');
    if (spokenLang?.code === 'OT' && !isInit) {
      this.getControl('data.othIndvLangPrefDtls').setValue(null);
    }
  }


  shouldShowInterpreterQuestions(): boolean {
    const writtenLang = this.getValue('data.writtenLangCd');
    const spokenLang = this.getValue('data.indvLangPrefCd');

    const writtenNeedsInterpreter = this.hasLanguageSelection(writtenLang) && !this.isEnglishLanguage(writtenLang);
    const spokenNeedsInterpreter = this.hasLanguageSelection(spokenLang) && !this.isEnglishLanguage(spokenLang);

    return writtenNeedsInterpreter || spokenNeedsInterpreter;
  }


  private isShowInterpreterHearingFields(isInit = false): void {
    const needInterpreterControl = this.getControl('data.mbrNeedIntrpretorSw');

    if (this.shouldShowInterpreterQuestions()) {
      needInterpreterControl.setValidators(Vs.required);
      needInterpreterControl.updateValueAndValidity();
      return;
    }

    needInterpreterControl.clearValidators();
    this.setInterpreterUsedFlag(false);
    this.setHearingLanguageRequired(false);

    if (!isInit) {
      needInterpreterControl.setValue(null);
      this.clearHearingLanguageValues();
    }

    needInterpreterControl.updateValueAndValidity();
  }


  needInterpreter(isInit = false) {
    this.isShowInterpreterHearingFields(isInit);
    if (!this.shouldShowInterpreterQuestions()) {
      return;
    }

    const interpreterNeed = this.getValue('data.mbrNeedIntrpretorSw');
    const isInterpreterNeeded = interpreterNeed?.code === 'Y';
    this.setInterpreterUsedFlag(isInterpreterNeeded);
    this.setHearingLanguageRequired(isInterpreterNeeded);

    if (!isInterpreterNeeded && !isInit) {
      this.clearHearingLanguageValues();
    }
  }


  private isEnglishLanguage(language): boolean {
    if (!language) {
      return false;
    }
    const code = (language.code || '')
    return code === 'EN' || code === '0';
  }


  private setInterpreterUsedFlag(isInterpreterNeeded: boolean): void {
    this.getControl('data.mbrIntrpretorSw').patchValue(
      isInterpreterNeeded
        ? { code: 'Y', value: 'Yes', spValue: 'Si' }
        : { code: 'N', value: 'No', spValue: 'No' }
    );
  }


  private setHearingLanguageRequired(isRequired: boolean): void {
    const hearingLanguageControl = this.getControl('data.hearLangPrefCd');
    if (isRequired) {
      hearingLanguageControl.setValidators(Vs.required);
    } else {
      hearingLanguageControl.clearValidators();
    }
    hearingLanguageControl.updateValueAndValidity();
  }


  private clearHearingLanguageValues(): void {
    this.getControl('data.hearLangPrefCd').setValue(null);
    this.getControl('data.othHearLangPrefDtls').setValue(null);
  }


  private hasLanguageSelection(language): boolean {
    if (!language) {
      return false;
    }
    const code = (language.code || '').toString().trim();
    return code.length > 0;
  }


  hearingLang(isInit = false) {
    const hearingLang = this.getValue('data.hearLangPrefCd');
    if (hearingLang?.code === 'OT' && !isInit) {
      this.getControl('data.othHearLangPrefDtls').setValue(null);
    }
  }


  onEdit() {
    this.showHouseholdAddressForm = true;
    this.processService.addressValidated = false;
    sessionStorage.removeItem('mademAddressValidated');    
    this.addressFormatValue = this.getValue('data.medAppealAddress.addressFormat')
    const countyValue = this.getValue('data.medAppealAddress.address.county');

    if (this.addressFormatValue?.code === 'MI') {
      const addrApoFpoControl = this.getControl('data.medAppealAddress.address.addrApoFpo');
      const addrAaAeApControl = this.getControl('data.medAppealAddress.address.addrAaAeAp');

      const tableVaraddrApoFpo = this.tables.MILITARYPOCODES.filter((item) => item.code === this.existingHouseholdMemberAddress?.addrApoFpo?.code);
      tableVaraddrApoFpo[0].value = this.existingHouseholdMemberAddress?.addrApoFpo?.value;
      addrApoFpoControl.patchValue(tableVaraddrApoFpo[0]);

      const tableVaraddrAaAeAp = this.tables.STATEMILITARY.filter((item) => item.code === this.existingHouseholdMemberAddress?.addrAaAeAp?.code);
      tableVaraddrAaAeAp[0].value = this.existingHouseholdMemberAddress?.addrAaAeAp?.value;
      addrAaAeApControl.patchValue(tableVaraddrAaAeAp[0]);

      this.getControl('data.medAppealAddress.address.addressLine1').patchValue(this.existingHouseholdMemberAddress?.addressLine1);
      this.getControl('data.medAppealAddress.address.addressLine2').patchValue(this.existingHouseholdMemberAddress?.addressLine2);
      this.getControl('data.medAppealAddress.address.zip').patchValue(this.existingHouseholdMemberAddress?.zip);

    } else if (countyValue?.value) {
      const countyControl = this.getControl('data.medAppealAddress.address.county');
      const tableCounty = this.tables.COUNTY.filter((item) => item.code === this.existingHouseholdMemberAddress?.county?.code);
      tableCounty[0].value = this.existingHouseholdMemberAddress?.county?.value;
      countyControl.patchValue(tableCounty[0]);
    }

    this.getControl('data.addressEditValidation').setValidators(Vs.required);
    this.getControl('data.addressEditValidation').updateValueAndValidity();
  }


  onSave() {
    this.showHouseholdAddressForm = false;
    this.addressFormatValue = this.getValue('data.medAppealAddress.addressFormat')
    this.existingHouseholdMemberAddress = this.getValue('data.medAppealAddress.address');
    this.existingHouseholdMemberAddress.zip = this.getValue('data.medAppealAddress.address.zip');


    if (this.existingHouseholdMemberAddress.zip && this.existingHouseholdMemberAddress.zip.length > 5) {
      this.householdZip = `${this.existingHouseholdMemberAddress.zip.substr(0, 5)}-${this.existingHouseholdMemberAddress.zip.substr(5, 4)}`;
    } else{
      this.householdZip = this.existingHouseholdMemberAddress.zip;
    }

    this.getControl('data.addressEditValidation').clearValidators();
    this.getControl('data.addressEditValidation').updateValueAndValidity();
    if(this.addressFormatValue?.code === 'MI') {
      const addressValidationCd = this.getControl('data.medAppealAddress.addressValidationCd', this.formGroup);
      addressValidationCd.setValue('MEA');
    }
  }


  onCancel() {
    this.showHouseholdAddressForm = false;
    this.getControl('data.medAppealAddress.addressFormat').patchValue(this.addressFormatValue);
    this.getControl('data.medAppealAddress.address').patchValue(this.existingHouseholdMemberAddress);
    
    // Re-enable/disable controls based on restored format
    const city = this.getControl('data.medAppealAddress.address.city');
    const state = this.getControl('data.medAppealAddress.address.state');
    const county = this.getControl('data.medAppealAddress.address.county');
    const addrApoFpo = this.getControl('data.medAppealAddress.address.addrApoFpo');
    const addrAaAeAp = this.getControl('data.medAppealAddress.address.addrAaAeAp');
    
    if (this.addressFormatValue?.code === 'MI') {
      // Military format: enable military fields, disable US fields
      addrApoFpo?.enable();
      addrAaAeAp?.enable();
      city?.disable();
      state?.disable();
      county?.disable();
    } else {
      // US format: enable US fields, disable military fields
      city?.enable();
      state?.enable();
      if (state?.value?.code === 'TN') {
        county?.enable();
      } else {
        county?.disable();
      }
      addrApoFpo?.disable();
      addrAaAeAp?.disable();
    }
    
    this.addressFormatChange('onLoad');
    this.getControl('data.addressEditValidation').clearValidators();
    this.getControl('data.addressEditValidation').updateValueAndValidity();
    if(this.addressFormatValue?.code === 'MI') {
      const addressValidationCd = this.getControl('data.medAppealAddress.addressValidationCd', this.formGroup);
      addressValidationCd.setValue('MEA');
    }
  }


  ngOnDestroy() {
    this.addressSub.unsubscribe();
  }

}